import { useEffect, type ReactElement } from 'react';
import {
  Autocomplete,
  Dialog,
  Input,
  Menu,
  Modal,
  ModalOverlay,
  SearchField,
  useFilter
} from 'react-aria-components';
import { useReports } from '../api/queries';
import { sectionPath, type NavSection, type SettingsSection } from '../app/nav';
import { MenuItem, MenuSection } from '../ui/Menu';

export interface PaletteAction {
  id: string;
  label: string;
  run: () => void;
}

const RECENT_LIMIT = 5;
const PALETTE_ATTR = 'data-command-palette';

export function CommandPalette({
  workspaceId,
  sections,
  settings,
  isOpen,
  onOpenChange,
  actions
}: {
  workspaceId: string;
  sections: NavSection[];
  settings: SettingsSection[];
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  actions: PaletteAction[];
}): ReactElement {
  const { contains } = useFilter({ sensitivity: 'base' });
  const reports = useReports(workspaceId);
  const recent = [...(reports.data?.reports ?? [])]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, RECENT_LIMIT);
  const close = () => onOpenChange(false);

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      className="fixed inset-0 z-dialog m-0 p-0 border-0 bg-scrim flex items-start justify-center pt-palette"
    >
      <Modal className="w-full max-w-xl m-0 p-0 border-0 bg-surface text-ink font-ui rounded-panel shadow-dialog">
        <Dialog
          aria-label="Command palette"
          {...{ [PALETTE_ATTR]: '' }}
          className="m-0 p-3 border-0 bg-surface outline-none flex flex-col gap-2 font-ui"
        >
          <Autocomplete filter={contains}>
            <SearchField
              aria-label="Search commands"
              autoFocus
              className="m-0 p-0 border-0 bg-surface flex"
            >
              <Input
                placeholder="Search reports, pages and actions"
                className="m-0 px-3 py-2 w-full border border-solid border-line-strong rounded-control bg-surface text-ink font-ui text-md outline-none appearance-none"
              />
            </SearchField>
            <Menu
              aria-label="Commands"
              className="m-0 p-1 border-0 bg-surface text-ink font-ui text-md outline-none max-h-palette-list overflow-auto"
            >
              <MenuSection title="Recent reports">
                {recent.map((r) => (
                  <MenuItem
                    key={`report-${r.id}`}
                    id={`report-${r.id}`}
                    textValue={r.title}
                    href={`/w/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(r.id)}`}
                    onAction={close}
                  >
                    {r.title}
                  </MenuItem>
                ))}
              </MenuSection>
              <MenuSection title="Go to">
                {sections.map((s) => (
                  <MenuItem
                    key={`section-${s.id}`}
                    id={`section-${s.id}`}
                    textValue={s.label}
                    href={sectionPath(s, workspaceId)}
                    onAction={close}
                  >
                    {s.label}
                  </MenuItem>
                ))}
                {settings.map((s) => (
                  <MenuItem
                    key={`settings-${s.id}`}
                    id={`settings-${s.id}`}
                    textValue={`Settings › ${s.label}`}
                    href={sectionPath(s, workspaceId)}
                    onAction={close}
                  >
                    {`Settings › ${s.label}`}
                  </MenuItem>
                ))}
              </MenuSection>
              <MenuSection title="Actions">
                {actions.map((a) => (
                  <MenuItem
                    key={`action-${a.id}`}
                    id={`action-${a.id}`}
                    textValue={a.label}
                    onAction={() => {
                      a.run();
                      close();
                    }}
                  >
                    {a.label}
                  </MenuItem>
                ))}
              </MenuSection>
            </Menu>
          </Autocomplete>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

/** Opens the palette on Ctrl/Cmd+K; ignored while focus is inside the open palette. */
export function usePaletteShortcut(open: () => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k' || !(event.ctrlKey || event.metaKey)) return;
      if (event.target instanceof Element && event.target.closest(`[${PALETTE_ATTR}]`)) return;
      event.preventDefault();
      open();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);
}
